// src/iap/initPurchases.ts
import axios from 'axios';
import * as appActions from '../Actions/app';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';

declare global {
  interface Window {
    CdvPurchase: any;
    cordova: any;
    Capacitor: any;
    document?: Document;
    __iapInitialized?: boolean;
    __iapLastVerifiedReceiptHash?: string;
  }
}

type MinimalUser = { id?: string | number; email?: string | null } | null;
type Dispatch = (a: any) => void;

const LOG = '[IAP]';
const log  = (...a: any[]) => console.log(LOG, ...a);
const warn = (...a: any[]) => console.warn(LOG, ...a);
const err  = (...a: any[]) => console.error(LOG, ...a);

const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/`;

const looksB64 = (s: any) =>
  typeof s === 'string' && s.length > 200 && /^[A-Za-z0-9+/=]+$/.test(s);

function pickB64(obj: any): string | undefined {
  if (!obj) return;
  if (looksB64(obj)) return obj;
  const keys = [
    'appStoreReceipt', 'appStoreReceiptBase64', 'latestReceipt', 'receipt',
    'receipt_data', 'base64', 'data', 'raw'
  ];
  for (const k of keys) if (looksB64(obj?.[k])) return obj[k];
  for (const v of Object.values(obj)) if (looksB64(v)) return v as string;
  return;
}

// Tiny cheap hash for dedupe (NOT cryptographic; just for “don’t spam verify”).
function cheapHash(s: string): string {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
  }
  // unsigned
  return (h >>> 0).toString(16);
}

function getAppReceiptViaExec(): Promise<string | undefined> {
  return new Promise((resolve) => {
    const exec = window?.cordova?.exec;
    if (!exec) { warn('cordova.exec not available'); return resolve(undefined); }
    log('exec → InAppPurchase.appStoreReceipt');
    exec(
      (res: any) => {
        const b64 = pickB64(res);
        log('exec ← appStoreReceipt:', b64 ? `ok(len=${b64.length})` : `no-b64`);
        resolve(b64);
      },
      (e: any) => { warn('exec appStoreReceipt failed', e); resolve(undefined); },
      'InAppPurchase',
      'appStoreReceipt',
      []
    );
  });
}

async function getReceiptB64(Cdv: any): Promise<string | undefined> {
  const { store, Platform } = Cdv;
  // Preferred: v13+ getter
  if (typeof store?.getReceipt === 'function') {
    try {
      const r = await store.getReceipt(Platform.APPLE_APPSTORE);
      const b64 = pickB64(r);
      log('store.getReceipt →', b64 ? `ok(len=${b64.length})` : 'no-b64');
      if (b64) return b64;
    } catch (e) {
      warn('store.getReceipt threw', e);
    }
  } else {
    log('store.getReceipt is undefined (JS bundled incorrectly or old adapter).');
  }
  // Fallback: call native directly
  return getAppReceiptViaExec();
}

async function verify(
  base64: string,
  userData: MinimalUser,
  dispatch: Dispatch,
  currentAuthToken?: () => Promise<string | undefined>,
  meta?: { reason?: 'approved'; txId?: string; origTxId?: string }
) {
  try {
    const token = (await currentAuthToken?.()) || '';
    if (!token) {
      warn('verify skipped: no auth token');
      return;
    }

    // Dedupe per receipt blob
    const h = cheapHash(base64);
    if (window.__iapLastVerifiedReceiptHash === h) {
      log('verify skipped: same receipt hash already verified');
      return;
    }
    window.__iapLastVerifiedReceiptHash = h;

    const url = `${baseUrl}receipts/verify`;
    const { data, status } = await axios.post(
      url,
      {
        platform: 'apple',
        receipt_data: base64,
        reason: meta?.reason,
        tx_id: meta?.txId,
        original_tx_id: meta?.origTxId,
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    log('verify ←', status, 'active=', !!data?.active, 'env=', data?.environment);
    dispatch(appActions.setUserData({
      ...(userData || {}),
      subscription_status: data?.active ? 'active' : 'expired',
    }));
  } catch (e: any) {
    err('verify failed', e?.response?.status, e?.message);
  }
}

function onDeviceReady(cb: () => void) {
  if ((window as any).cordova) {
    document.addEventListener('deviceready', cb, { once: true });
  } else {
    cb();
  }
}

export function setupIAP(
  userData: MinimalUser,
  dispatch: Dispatch,
  currentAuthToken?: () => Promise<string | undefined>
) {
  if (window.__iapInitialized) { log('already initialized'); return; }
  window.__iapInitialized = true;

  onDeviceReady(async () => {
    const Cdv = window.CdvPurchase;
    if (!Cdv?.store) { err('CdvPurchase.store missing'); return; }

    const { store, ProductType, Platform, LogLevel } = Cdv;
    try { store.verbosity = LogLevel.DEBUG; } catch {}

    store.applicationUsername = () => (userData?.id ?? userData?.email ?? '') + '';

    store.register([
      { id: 'com.museflow.monthly',  type: ProductType.PAID_SUBSCRIPTION, platform: Platform.APPLE_APPSTORE, group: 'museflow' },
      { id: 'com.museflow.3months',  type: ProductType.PAID_SUBSCRIPTION, platform: Platform.APPLE_APPSTORE, group: 'museflow' },
      { id: 'com.museflow.annual',   type: ProductType.PAID_SUBSCRIPTION, platform: Platform.APPLE_APPSTORE, group: 'museflow' },
    ]);

    store.when().approved(async (tx: any) => {
      log('approved:', {
        txId: tx?.transactionId,
        orig: tx?.originalTransactionId,
        plat: tx?.platform,
        products: (tx?.products||[]).map((p: any)=>p.id)
      });

      try {
        // Prefer receipt attached to tx, else pull app receipt
        let b64 = pickB64(tx?.receipt);
        log('approved: tx.receipt present?', !!b64);
        if (!b64) b64 = await getReceiptB64(Cdv);

        if (b64) {
          await verify(b64, userData, dispatch, currentAuthToken, {
            reason: 'approved',
            txId: tx?.transactionId,
            origTxId: tx?.originalTransactionId,
          });
        } else {
          warn('approved: no app receipt available to verify');
        }
      } finally {
        try { tx.finish(); log('approved: finish()'); } catch (e) { err('finish() failed', e); }
        try { await store.update(); log('approved: store.update()'); } catch {}
      }
    });

    store.error((e: any) => err('Store Error:', e));
    store.ready(() => log('Store ready'));

    try {
      log('initialize([APPLE])');
      await store.initialize([Platform.APPLE_APPSTORE]);
      log('update()');
      await store.update();

      // IMPORTANT: removed cold-start verification
      // We only verify on actual approved transactions.
      log('startup: skipping verify (only verifying on purchase approval)');
    } catch (e) {
      err('initialize/update failed', e);
    }
  });
}
